
import React from 'react';
import { Chip, Grid, TextField, Typography } from '@material-ui/core';

import { RevisionContext } from '../RevisionContext';
import { HomeContext } from '../../../Home/HomeContext';
import { BasicConfirmDialog } from '../../../../shared/components/BasicConfirmDialog';
import { theme } from '../../../../theme';
import { BatchCorrectionActions } from '../components/BatchCorrectionActions';
import { SourceModel } from '../../../../Models/SourceModel';
import SourceService from '../../../../Services/SourceService';
import { Autocomplete } from '@material-ui/lab';
import { BatchCorrectionRemove } from '../components/BatchCorrectionRemove';
import CategorySourceService from '../../../../Services/CategorySourceService';
import { SourceBatchModel, sourceBatchModelInitial } from '../../../../Models/SourceBatchModel';
import { BatchCorrectionAction } from '../enum/BatchCorrectionAction';
import { AxiosError, AxiosResponse } from 'axios';
import { BatchDto } from '../../../../Models/BatchDto';
import { BatchModel } from '../../../../Models/BatchModel';
import { BatchCorrectionContext } from '../BatchCorrectionContext';

export function SourceBatchCorrection() {

    const revisionContext = React.useContext(RevisionContext);
    const homeContext = React.useContext(HomeContext);
    const batchCorrectionContext = React.useContext(BatchCorrectionContext);

    const [sourceListAdd, setSourceListAdd] = React.useState<SourceModel[]>([]);
    const [sourceAddSelected, setSourceAddSelected] = React.useState<SourceModel[]>([]);

    const [sourceListDelete, setSourceListDelete] = React.useState<BatchDto[]>([]);
    const [sourceDeleteSelected, setSourceDeleteSelected] = React.useState<BatchModel[]>([]);

    const [sourceNotDefinedError, setSourceNotDefinedError] = React.useState<boolean>(false);

    const [batchCorrectionAction, setBatchCorrectionAction] = React.useState<BatchCorrectionAction>(BatchCorrectionAction.ADD);

    React.useEffect(() => {
        const open = revisionContext.sourceBatchOpenDialog;
        setSourceNotDefinedError(false);

        if (open && batchCorrectionAction === BatchCorrectionAction.ADD && sourceListAdd.length === 0)
            fetchClientSourceList();

        if (open && batchCorrectionAction === BatchCorrectionAction.REMOVE && sourceListDelete.length === 0)
            fetchCategorySourceList();
    }, [revisionContext.sourceBatchOpenDialog, batchCorrectionAction]);

    const fetchClientSourceList = () => {
        if (revisionContext.filterClassification.clientId)
            SourceService.sourceService(revisionContext.filterClassification.clientId)
                .getAll()
                .then((res: AxiosResponse) => { setSourceListAdd(res.data); })
                .catch((error: AxiosError) => { homeContext.showError('', error); });
    };

    const fetchCategorySourceList = () => {
        const sourceBatch = sourceBatchModelInitial;
        sourceBatch.categoriesId = revisionContext.ids;

        CategorySourceService.getSourceBatch(sourceBatch)
            .then((res: AxiosResponse) => { setSourceListDelete(res.data.sourcesDto); })
            .catch((error: AxiosError) => { homeContext.showError('', error); });
    };

    const handleSaveButton = () => {
        if (!hasError()) {
            homeContext.setOpenLoadingDialog(true);

            const sourceBatch = { ...sourceBatchModelInitial };
            sourceBatch.categoriesId = revisionContext.ids;

            if (batchCorrectionAction === BatchCorrectionAction.ADD) {
                sourceBatch.sources = sourceAddSelected;
                updateSourceBatch(sourceBatch);
            } else {
                sourceBatch.sources = sourceDeleteSelected;
                deleteSourceBatch(sourceBatch);
            }
        }
    };

    const updateSourceBatch = (sourceBatch: SourceBatchModel) => {
        CategorySourceService.insertSourceBatch(sourceBatch)
            .then(() => { showSuccess(); })
            .catch((error: AxiosError) => { homeContext.showError('', error); })
            .finally(() => { homeContext.setOpenLoadingDialog(false); });
    };

    const deleteSourceBatch = (sourceBatch: SourceBatchModel) => {
        CategorySourceService.deleteSourceBatch(sourceBatch)
            .then(() => { showSuccess(); })
            .catch((error: AxiosError) => { homeContext.showError('', error); })
            .finally(() => { homeContext.setOpenLoadingDialog(false); });
    };

    const showSuccess = () => {
        handleClearModal();
        homeContext.setOpenLoadingDialog(false);
        homeContext.showSuccess(`As alterações foram realizadas para todas as ${revisionContext.ids.length} categorizações selecionadas com sucesso!`);
        batchCorrectionContext.setCallSearch(true);
    };

    const handleClearModal = () => {
        revisionContext.setSourceBatchOpenDialog(false);
        setSourceNotDefinedError(false);
        setSourceListAdd([]);
        setSourceAddSelected([]);
        setSourceListDelete([]);
        setSourceDeleteSelected([]);
        setBatchCorrectionAction(BatchCorrectionAction.ADD);
    };

    const hasError = () => {
        let error = false;

        if (batchCorrectionAction === BatchCorrectionAction.ADD)
            error = (!sourceAddSelected || sourceAddSelected.length == 0);

        setSourceNotDefinedError(error);
        return error;
    };

    const getModal = () => {
        return (
            <Grid container spacing={3}>
                <BatchCorrectionActions
                    value={batchCorrectionAction}
                    setValue={setBatchCorrectionAction} />
                {batchCorrectionAction === BatchCorrectionAction.ADD && getSourceAddInput()}
                {batchCorrectionAction === BatchCorrectionAction.REMOVE && getSourceRemoveTags()}
            </Grid>
        );
    };

    const getSourceAddInput = () => {
        return (
            <Grid item xs={12}>
                <Autocomplete
                    multiple
                    id="sources"
                    options={sourceListAdd}
                    getOptionLabel={(option) => option.name}
                    getOptionSelected={(option, value) => option.id === value.id}
                    filterSelectedOptions
                    value={sourceAddSelected}
                    onChange={(_, value: SourceModel[]) => { setSourceAddSelected(value); }}
                    noOptionsText="Sem resultados"
                    loadingText="Carregando..."
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                key={index}
                                variant="outlined"
                                label={option.name}
                                {...getTagProps({ index })} />
                        ))
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label="Fontes"
                            fullWidth
                            error={sourceNotDefinedError && (!sourceAddSelected || sourceAddSelected.length == 0)}
                            helperText={sourceNotDefinedError && (!sourceAddSelected || sourceAddSelected.length == 0)
                                ? 'Selecione pelo menos uma fonte'
                                : ''} />
                    )} />
            </Grid>
        );
    };

    const getSourceRemoveTags = () => {
        return (
            <>
                <Grid item xs={12}>
                    <Typography variant='subtitle1'>
                        Excluir fontes
                    </Typography>
                    <Typography variant='caption'>
                        *As fontes sem preenchimento não são comuns às categorizações selecionadas
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <BatchCorrectionRemove
                        list={sourceListDelete}
                        setList={setSourceListDelete}
                        removed={sourceDeleteSelected}
                        setRemoved={setSourceDeleteSelected} />
                </Grid>
            </>
        );
    };

    return (
        <BasicConfirmDialog
            title={'Alteração de fontes'}
            description={'Exclua ou adicione fontes para as categorias selecionadas'}
            handleFirstButton={() => handleClearModal()}
            handleSecondButton={() => handleSaveButton()}
            textFirstButton='DESCARTAR'
            textSecondButton='SALVAR'
            open={revisionContext.sourceBatchOpenDialog}
            children={getModal()}
            cssProperties={{
                minWidth: '800px',
                maxWidth: '800px',
                padding: '1rem',
                colorFirstButton: theme.palette.primary.main,
                justifyButtons: 'flex-end',
                titleVariant: 'h4',
                descriptionVariant: 'body1'
            }} />
    );
}